import {
  useGetAccountContacts,
  useSendAccountAssociationCode,
  useValidateAccountAssociationContact,
} from '@/hooks/api/account-association.hooks';
import { useForm } from '@deadcow-enterprises/react-forms';
import { Loader } from '@nosinovacao/fe-icons';
import {
  Button,
  Captcha,
  ErrorCodes,
  If,
  Input,
  logger,
  type NOSIDError,
  useHasRequiredFormError,
  useI18n,
} from '@nosinovacao/nosid-mfe-common';
import { type AxiosError } from 'axios';
import z from 'zod';
import { useServicesContext } from './context';

export function ServicesNavSheetHolderInputContent() {
  const { mutateAsync: identifyContact } = useGetAccountContacts();
  const { mutateAsync: validateContact } = useValidateAccountAssociationContact();
  const { mutateAsync: sendCode, captcha } = useSendAccountAssociationCode();
  const {
    flowData: { associationType, ...rest },
    setFlow,
  } = useServicesContext();
  const form = useForm(
    {
      associationType: {
        value: associationType,
      },
      value: {
        value: '',
        id: 'holder-input',
        validator: ({ associationType }) => {
          const schema = z.string().min(1, 'Services.AddServices.HolderInput.Errors.ValueRequired');

          switch (associationType) {
            case 'Email':
              return schema.emailAddress('Services.AddServices.HolderInput.Errors.InvalidEmail');
            case 'Phone':
              return schema.phone('Services.AddServices.HolderInput.Errors.InvalidPhone');
            case 'Nif':
              return schema.nif('Services.AddServices.HolderInput.Errors.InvalidNif');
            default:
              return schema;
          }
        },
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );
  async function handleSubmit({ value }: { value: string }) {
    try {
      const isAssociationWithContactSelection =
        associationType === 'Nif' || associationType === 'Passport';
      if (isAssociationWithContactSelection) {
        const contacts = await identifyContact({ type: associationType, value });
        setFlow('contact-selection', {
          ...rest,
          associationType,
          contactSelectionContacts: contacts,
          selectedContact: value,
          code: '',
        });
        return;
      }
      await validateContact({ type: associationType, value });
      if (captcha.challenge && !captcha.token) return;
      await sendCode({ captcha, contactId: undefined, targetId: value, type: associationType });
      setFlow('code-input', {
        ...rest,
        associationType,
        selectedContact: value,
        contactSelectionContacts: [],
        code: '',
      });
    } catch (err) {
      const errorCode = (err as AxiosError<NOSIDError>).response?.data.ErrorCode;
      if (errorCode === ErrorCodes.PersonNotFound || errorCode === ErrorCodes.UserNotFound)
        form.addError(
          'value',
          `Services.AddServices.HolderInput.Errors.PersonNotFound.${associationType}`,
        );
    }
  }
  const { t } = useI18n();
  const hasRequiredFormError = useHasRequiredFormError(form);
  if (!associationType) {
    setFlow('main');
    return null;
  }
  return (
    <form
      className="flex size-full flex-col gap-6 max-tablet:pb-8"
      onSubmit={form.handleSubmit(handleSubmit)}
      data-tid="form-holder-input"
    >
      <Input
        label={`Services.AddServices.HolderInput.Label.${associationType}`}
        id={form.ids.value ?? 'value'}
        errors={form.errors.value}
        value={form.value.value}
        skin="light"
        data-tid="input-holder-value"
        onChange={(ev) => form.setValue('value', ev.target.value)}
      />
      <If condition={captcha.challenge}>
        <Captcha onChange={captcha.setToken} />
      </If>
      <Button
        className="mt-auto"
        skin="light"
        disabled={hasRequiredFormError || form.loading}
        data-tid="button-submit-holder"
      >
        <If condition={form.loading} otherwise={t('Services.AddServices.HolderInput.Confirm')}>
          <Loader width={24} height={24} />
        </If>
      </Button>
    </form>
  );
}
